import { HorizontalSeparator, ZCDCard, ZCDSpinner } from '@zencity/common-ui';
import { useAsyncEffect } from 'customHooks/hooks';
import React, { ReactElement } from 'react';
import { SelectedDateRange } from 'types/misc';
import { fetchAspectScores } from 'services/blockwiseReport/scores';
import { ScoreRow } from 'screens/WidgetGenerator/reportGeneratorComponents/components/ScoresOverTimeByArea/ScoresList/ScoreRow/ScoreRow';
import { SurveyCycle } from 'screens/WidgetGenerator/utils/misc';
import { AspectScores } from 'types/blockwiseScores';
import {
  reduceAllAspectScoresByCurrentAspectScore,
  findSelectedCycleAspectScores,
} from 'screens/WidgetGenerator/reportGeneratorComponents/components/ScoresOverTimeByArea/ScoresList/ScoresList.helpers';
import { ScoreOverTimeByAreaContext } from 'contexts/ScoresOverTimeByAreaContext';
import { useTranslation } from 'react-i18next';
import styles from './ScoresList.module.scss';

interface Props {
  surveyGroupId: number;
  selectedDateRange: SelectedDateRange;
  cycles: SurveyCycle[];
  divisionId?: number;
  parentDivisionId?: number;
}

// eslint-disable-next-line max-lines-per-function,max-statements
export const ScoresList = (props: Props): ReactElement => {
  const { surveyGroupId, selectedDateRange, cycles, divisionId, parentDivisionId } = props;
  const { t: translate } = useTranslation();

  const fetchAllBlockwiseAspectScores = async (): Promise<AspectScores[]> => {
    const promises = cycles.reduce<Promise<AspectScores>[]>((aspectScoresPromises, cycle) => {
      if (!cycle.displayOnDashboard) {
        return aspectScoresPromises;
      }
      return [...aspectScoresPromises, fetchAspectScores(surveyGroupId, cycle.startDate, cycle.endDate, divisionId)];
    }, []);
    const allCycleAspectScores = await Promise.all(promises);
    return allCycleAspectScores;
  };

  const {
    fetchedData: allCycleAspectScores,
    isLoading,
    error,
  } = useAsyncEffect(fetchAllBlockwiseAspectScores, [surveyGroupId, cycles, divisionId]);

  const fetchParentDivisionBlockwiseAspectScores = async (): Promise<AspectScores[]> => {
    if (!parentDivisionId) {
      return [];
    }

    const aspectScoresPromises = cycles.reduce<Promise<AspectScores>[]>((accumulator, cycle) => {
      if (!cycle.displayOnDashboard) {
        return accumulator;
      }
      return [...accumulator, fetchAspectScores(surveyGroupId, cycle.startDate, cycle.endDate, parentDivisionId)];
    }, []);
    const parentDivisionCycleAspectScores = await Promise.all(aspectScoresPromises);
    return parentDivisionCycleAspectScores;
  };

  const {
    fetchedData: parentDivisionCycleAspectScores = [],
    isLoading: parentDivisionIsLoading,
    error: parentDivisionError,
  } = useAsyncEffect(fetchParentDivisionBlockwiseAspectScores, [surveyGroupId, cycles, parentDivisionId]);

  const displayError = error || parentDivisionError;
  if (displayError) {
    return (
      <ZCDCard variant="danger">
        <p>{translate('widgetGenerator.widgets.scoresOverTimeByArea.errorMessage')}</p>
      </ZCDCard>
    );
  }

  const displaySpinner = isLoading || parentDivisionIsLoading;
  if (displaySpinner) {
    return <ZCDSpinner />;
  }

  const displayWarning = !allCycleAspectScores || allCycleAspectScores.length === 0;
  if (displayWarning) {
    return (
      <ZCDCard variant="warn">
        <p>{translate('widgetGenerator.widgets.scoresOverTimeByArea.noCycleScores')}</p>
      </ZCDCard>
    );
  }

  const selectedCycleAspectScores = findSelectedCycleAspectScores(allCycleAspectScores, selectedDateRange);
  const { aspect_scores: aspectScores } = selectedCycleAspectScores;

  return (
    <div className={styles.container}>
      {aspectScores.map((aspectScore, index) => {
        const allCycleAspectScoresForCurrent = reduceAllAspectScoresByCurrentAspectScore(
          aspectScore.aspect_id,
          allCycleAspectScores,
        );
        const parentDivisionCycleAspectScoresForCurrent = reduceAllAspectScoresByCurrentAspectScore(
          aspectScore.aspect_id,
          parentDivisionCycleAspectScores,
        );
        return (
          <div key={aspectScore.aspect_id}>
            <ScoreOverTimeByAreaContext.Provider
              value={{
                allAspectScores: allCycleAspectScores,
                allAspectScoresWithDateRange: allCycleAspectScoresForCurrent,
                parentDivisionAspectScoresWithDateRange: parentDivisionCycleAspectScoresForCurrent,
                selectedAspectScore: aspectScore,
                selectedDateRange,
              }}
            >
              <ScoreRow />
            </ScoreOverTimeByAreaContext.Provider>
            {index < aspectScores.length && <HorizontalSeparator />}
          </div>
        );
      })}
    </div>
  );
};
